import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import TrippinPostGrid from '../components/TrippinPostGrid';
import BottomNav from './BottomNav';
import { Menu } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { StarIcon } from '@heroicons/react/24/solid';
import VisitedCountriesMap from './VisitedCountriesMap';
import countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countriesLib.registerLocale(enLocale);

function toCountryCode(countryName) {
  if (!countryName) return null;
  const code = countriesLib.getAlpha2Code(countryName, 'en');
  return code ? code.toLowerCase() : null;
}

function Profile() {
  const { userIdPar } = useParams();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(userIdPar || null);
  const [countries, setCountries] = useState([]); // country ISO codes (alpha-2, lowercase)
  const [loadingCountries, setLoadingCountries] = useState(true);
  const ratingContainerRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const { userId: loggedInUserId, initialized, setAccessToken } = useAuth();

  const fetchBlockStatus = async () => {
    try {
      const res = await axiosInstance.get(`/block/status`, {
        params: { targetUserId: userId },
      });
      setIsBlocked(res.data.isBlocked);
    } catch (error) {
      console.error('Error fetching block status:', error);
    }
  };

  const [profileData, setProfileData] = useState(null);
  const [employeeBusinesses, setEmployeeBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [canViewLists, setCanViewLists] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [myRatingForUser, setMyRatingForUser] = useState(0);
  const [trippinPosts, setTrippinPosts] = useState([]);
  // settings now navigates to a dedicated route instead of inline rendering
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryTrippins, setCountryTrippins] = useState([]);
  const [loadingCountryTrippins, setLoadingCountryTrippins] = useState(false);
  const [countryOffset, setCountryOffset] = useState(0);
  const COUNTRY_FETCH_LIMIT = 10;
  const hasMoreTrippins = useRef(true);
  const observer = useRef();
  const [activeTab, setActiveTab] = useState('trippins');
  const tabs = ['trippins', 'map'];

  const handleTabKeyDown = (e) => {
    const currentIndex = tabs.indexOf(activeTab);
    if (e.key === 'ArrowRight') {
      const nextIndex = (currentIndex + 1) % tabs.length;
      setActiveTab(tabs[nextIndex]);
      setTimeout(() => document.getElementById(`tab-${tabs[nextIndex]}`)?.focus(), 0);
    }
    else if (e.key === 'ArrowLeft') {
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[prevIndex]);
      setTimeout(() => document.getElementById(`tab-${tabs[prevIndex]}`)?.focus(), 0);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await axiosInstance.get('/profile/countries/get', {
        params: { userId }
      });
      const countryCodes = res.data.countries || [];
      setCountries(countryCodes);
      console.log('Fetched countries (ISO codes):', countryCodes);
      setLoadingCountries(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoadingCountries(false);
    }
  };

  const fetchTrippinsByCountry = async (countryIso2) => {
    const country = String(countryIso2 || '').trim().toUpperCase();
    if (!country) {
      console.error('fetchTrippinsByCountry called with an empty country!');
      return;
    }

    if (loadingCountryTrippins) return;

    try {
      setLoadingCountryTrippins(true);
      const viewerId = loggedInUserId;
      if (!viewerId || !userId) {
        console.error('Missing user context.');
        return;
      }

      // Accumulate ALL trippin ids for this country
      const PAGE = 50;
      let offset = 0;
      const idSet = new Set();
      // optional: keep minimal cache for faster open later (not required)
      // const initialMap = {};

      // paginate until backend runs out
      /* eslint-disable no-constant-condition */
      while (true) {
        const { data: batch = [] } = await axiosInstance.get(`/profile/by-country/${userId}`, {
          params: { country, viewerId, limit: PAGE, offset },
        });
        batch.forEach((t) => {
          if (t && t.trippin_post_id) idSet.add(t.trippin_post_id);
        });
        // batch smaller than page => done
        if (batch.length < PAGE) break;
        offset += PAGE;
        // safety: avoid runaway
        if (offset > 1000) break;
      }

      const allIds = Array.from(idSet);
      if (allIds.length === 0) {
        alert('No Trippins found for this country.');
        return;
      }

      // Open the Trippin overlay with this filtered list
      const domNode = document.getElementById('trippin-root');
      if (!domNode) {
        console.warn('trippin-root container not found');
        return;
      }
      const { mount, unmount } = await import('./TrippinPost/index.js');
      mount(domNode, {
        trippin_ids: allIds,
        selectedTrippinId: allIds[0],
        source: isOwner ? 'ownProfile' : 'profile',
        offset: 0,
        // initialTrippinDataMap: initialMap, // optional if we prefill
        onExit: () => unmount(),
      });

    } catch (err) {
      console.error('Error fetching Trippins by country:', err);
    } finally {
      setLoadingCountryTrippins(false);
    }
  };

  // Intersection Observer to detect when the last post is visible
  const lastTrippinElementRef = useRef(null);

  useEffect(() => {
    if (!selectedCountry || countryTrippins.length === 0) return;

    const observerCallback = (entries) => {
      if (entries[0].isIntersecting && hasMoreTrippins.current) {
        fetchTrippinsByCountry(selectedCountry);
      }
    };

    observer.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (lastTrippinElementRef.current) {
      observer.current.observe(lastTrippinElementRef.current);
    }

    return () => observer.current && observer.current.disconnect();
  }, [selectedCountry, countryTrippins]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/profile/${userId}`);
      setProfileData(res.data);
      setEmployeeBusinesses(res.data.employeeBusinesses || []);
      console.log(employeeBusinesses);
      console.log('Profile Data:', res.data);
      setIsOwner(res.data.isOwner);
      setCanViewLists(res.data.canViewLists);
      setProfilePicture(res.data.signedUrl);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setLoading(false);
    }
  };

  const handleRatingSubmit = async (newRating) => {
    try {
      const res = await axiosInstance.post(`/profile/${userId}/rate`, { rating: newRating });
      setMyRatingForUser(res.data.rating);
    } catch (err) {
      console.error('Error rating user:', err);
    }
  };

  const loadTrippinPosts = async () => {
    try {
      const res = await axiosInstance.get(`/trippin/user/${userId}?sort=newest`);
      setTrippinPosts(res.data);
    } catch (err) {
      console.error('Error fetching trippin posts:', err);
    }
  };

  const fetchMyRatingForUser = async () => {
    try {
      const raterId = loggedInUserId
      const res = await axiosInstance.get(`/profile/${raterId}/for/${userId}`);
      if (res.data && typeof res.data.rating === 'number') {
        setMyRatingForUser(res.data.rating);
      } else {
        setMyRatingForUser(0);
      }
    } catch (err) {
      console.error('Error fetching rating:', err);
    }
  };

  useEffect(() => {
    if (!loggedInUserId && initialized) {
      console.error('User not logged in. Redirecting to login.');
      navigate('/login', { replace: true });
      return;
    }

    if (!userIdPar) {
      setUserId(loggedInUserId);
    } else {
      setUserId(userIdPar);
    }
  }, [userIdPar, navigate, loggedInUserId, initialized]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchCountries();
      if (userId !== loggedInUserId) {
        fetchMyRatingForUser();
        fetchBlockStatus();
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!loading && profileData) {
      loadTrippinPosts();
    }
  }, [loading, profileData]);

  const openHamburgerMenu = () => {
    // Navigate to settings route carrying minimal state
    navigate('/settings', {
      state: {
        from: 'profile',
        isOwner,
        employeeBusinesses,
        username,
        userId,
        isBlocked,
      },
    });
  };

  if (loading) {
    return (
      <>
        <div className="flex w-full h-screen items-center justify-center">
          <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
        </div>
        <BottomNav />
      </>
    );
  }

  if (!profileData) {
    return (
      <>
        <div className="flex w-full h-screen items-center justify-center px-4 text-center">
          <div className="text-black dark:text-white">Profile not found</div>
        </div>
        <BottomNav />
      </>
    );
  }

  const {
    username,
    stalking_count,
    stalkers_count,
    trippin_count,
    hasFlash,
  } = profileData;

  if (loadingCountries) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
      </div>
    );
  }

  // Settings is now shown via route; we do not render it inline here

  return (
    <div className="tailwind flex flex-col h-screen max-w-4xl mx-auto px-4 pt-14 pb-20">
      <div className="flex flex-col items-center relative">
        <button
          onClick={openHamburgerMenu}
          aria-label="Menu"
          className="absolute top-0 right-0 p-2 rounded-md focus:outline-none"
        >
          <Menu className="w-6 h-6 text-black dark:text-gray-100" />
        </button>
        <img
          src={profilePicture || '/assets/images/default_profile.png'}
          alt="Profile"
          className={`w-20 h-20 rounded-full object-cover shadow ${hasFlash ? 'ring-4 ring-mainColor dark:ring-mainLightColor ring-offset-4 ring-offset-white dark:ring-offset-darkBackground' : ''} cursor-pointer`}
          onClick={() => {
            if (hasFlash) navigate(`/user-flashes/${userId}`);
          }}
        />
        <h2 className="mt-4 text-2xl font-bold text-black dark:text-white">{profileData.username}</h2>
        <div className="mt-4 flex items-start justify-center gap-10 text-center">
          {canViewLists && (
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/profile/${userId}/stalking`)}
            >
              <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{stalking_count}</div>
              <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Stalking</div>
            </div>
          )}
          {canViewLists && (
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/profile/${userId}/stalkers`)}
            >
              <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{stalkers_count}</div>
              <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Stalkers</div>
            </div>
          )}
          <div>
            <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{trippin_count}</div>
            <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Trippins</div>
          </div>
        </div>
      </div>

      {!isOwner && (
        <div className="mt-6 flex flex-col items-center">
          {/* <p className="text-sm text-black dark:text-gray-100 mb-2">Your rating for {username}:</p> */}
          <div
            className="flex relative"
            ref={ratingContainerRef}
            onMouseLeave={() => setHoverRating(null)}
            onTouchMove={(e) => {
              const container = ratingContainerRef.current;
              if (!container) return;
              const rect = container.getBoundingClientRect();
              const touchX = e.touches[0].clientX - rect.left;
              const starWidth = rect.width / 5;
              const touchedStar = Math.ceil(touchX / starWidth);
              if (touchedStar < 1) setHoverRating(0);
              else if (touchedStar > 5) setHoverRating(5);
              else setHoverRating(touchedStar);
            }}
            onTouchEnd={() => handleRatingSubmit(hoverRating || 0)}
          >
            {[1, 2, 3, 4, 5].map((starValue) => (
              <label key={starValue} className="cursor-pointer">
                <input
                  type="radio"
                  name="rating"
                  value={starValue}
                  className="hidden"
                  onClick={() => handleRatingSubmit(starValue)}
                />
                <StarIcon
                  className={`w-7 h-7 transition-colors ${starValue <= (hoverRating || myRatingForUser)
                      ? 'text-mainLightColor'
                      : 'text-white'
                    }`}
                  onMouseEnter={() => setHoverRating(starValue)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {!isOwner && isBlocked ? (
        <div className="mt-10 flex-1 flex items-center justify-center px-6 text-center">
          <div className="text-black dark:text-white">
            You’ve blocked this user. Unblock them from the menu to view their Trippins.
          </div>
        </div>
      ) : (
        <>

          <div className="mt-6">
            <div
              role="tablist"
              aria-label="Profile sections"
              className="flex items-center justify-evenly gap-6 border-b border-gray-200 dark:border-gray-700"
            >
              {tabs.map((tab) => (
                <button
                  key={tab}
                  id={`tab-${tab}`}
                  role="tab"
                  aria-selected={activeTab === tab}
                  aria-controls={`panel-${tab}`}
                  tabIndex={activeTab === tab ? 0 : -1}
                  onKeyDown={handleTabKeyDown}
                  onClick={() => setActiveTab(tab)}
                  className={`-mb-px px-10 pb-3 pt-2 text-base font-medium transition-colors border-b-2 
                ${activeTab === tab
                      ? 'border-black dark:border-white text-black dark:text-white'
                      : 'border-transparent text-gray-600 dark:text-gray-300'
                    }`}
                >
                  {tab === 'trippins' ? 'Trippins' : 'Map'}
                </button>
              ))}
            </div>
          </div>

          <div
            role="tabpanel"
            id="panel-trippins"
            aria-labelledby="tab-trippins"
            hidden={activeTab !== 'trippins'}
          >
            <div className="mt-8">
              <TrippinPostGrid
                posts={trippinPosts}
                profilePicture={profileData.signedUrl}
                username={profileData.username}
                isOwner={isOwner}
              />
            </div>
          </div>

          <div
            role="tabpanel"
            id="panel-map"
            aria-labelledby="tab-map"
            hidden={activeTab !== 'map'}
            className={activeTab === 'map' ? "flex-1 flex items-center justify-center" : ""}
          >
            <VisitedCountriesMap
              countries={countries.map((c) => c.toUpperCase())}
              onCountryClick={(iso2) => fetchTrippinsByCountry(String(iso2).trim(), true)}
            />
          </div>

        </>
      )}

      <BottomNav />
    </div>
  );
}

export default Profile;